import { Form as FormikForm, Formik, FormikErrors, FormikProps, FormikTouched } from 'formik';
import React, { useEffect, useState } from 'react';
import { Col, Row, Stack } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { BackButton } from '../../../../../components/buttons/BackButton';
import { SimpleButton } from '../../../../../components/buttons/SimpleButton';
import Stepper from '../../../../../components/stepper/Stepper';
import { useToast } from '../../../../../context/ToastContext';
import {
  useCreateDocumentMutation,
  useGetDocumentQuery,
  useUpdateDocumentMutation
} from '../../../../../services/endpoints/content/document';
import { getCurrentUser } from '../../../../../services/helper';
import { getErrorMessage } from '../../../../../utils/utils';
import { FadeIn } from '../../../../animations/FadeIn';
import { DocumentForm } from './DocumentForm';
import { userCanEditCreateDelete_Documents } from './DocumentsView';

const steps = [{ name: 'Details', form: DocumentForm }];

export interface FormValues {
  id?: number;
  title: string;
  description: string;
  file: File;
}

export interface StepProps {
  errors: Partial<FormikErrors<FormValues>>;
  touched: Partial<FormikTouched<FormValues>>;
  values?: FormValues;
  setFieldValue?: (field: string, value: any, shouldValidate?: boolean) => void;
  readOnly?: boolean;
}

interface AddEditDocumentProps {
  editing?: boolean;
}

export const AddEditDocument: React.FC<AddEditDocumentProps> = ({ editing }) => {
  const params = useParams();
  // const urlParams = new URLSearchParams(location.search);

  const [currentStep, setCurrentStep] = useState(0);
  const { addToast } = useToast();
  const navigate = useNavigate();

  const [createDocument] = useCreateDocumentMutation();
  const [updateDocument] = useUpdateDocumentMutation();
  const { data, error, isLoading } = useGetDocumentQuery(Number(params.id!), {
    skip: !editing || !params.id
  });

  const currentUser = getCurrentUser();

  const [initialValues, setInitialValues] = useState<FormValues>({
    title: '',
    description: '',
    file: {} as File
  });

  const validationSchemas = [
    Yup.object({
      title: Yup.string().required('Title is required')
    })
  ];

  useEffect(() => {
    if (editing && error && !isLoading) {
      addToast('Error while loading document', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, error, isLoading]);

  useEffect(() => {
    if (data && data.document) {
      const { document } = data;
      setInitialValues({
        id: document.id,
        title: document.title,
        description: document.description,
        file: document.file
      });
    }

    if (!userCanEditCreateDelete_Documents(currentUser)) {
      addToast('You have no permissions to edit this data', 'error');
      navigate(`/admin/content/documents`);
    }
  }, [data]);

  const handleSubmit = async (formValue: FormValues) => {
    try {
      const documentData = {
        ...formValue,
        title: formValue.title ?? undefined,
        description: formValue.description ?? undefined,
        file: formValue.file ?? undefined
      };
      if (editing) {
        const res = await updateDocument(documentData).unwrap();
        if (res && res.document) {
          addToast(`Document updated succesfully`, 'success');
          navigate(`/admin/content/documents/${data?.document.id}`);
        }
      } else {
        const res = await createDocument(documentData).unwrap();
        if (res && res.document) {
          navigate(`/admin/content/documents/${res.document.id}`);
          addToast('Document created succesfully', 'success');
        } else throw new Error('A problem happened while creating document');
      }
    } catch (e) {
      addToast(getErrorMessage(e), 'error');
    }
  };

  const handleNext = async (
    validateForm: () => Promise<FormikErrors<FormValues>>,
    setTouched: (touched: FormikTouched<FormValues>, shouldValidate?: boolean) => void,
    values: FormValues
  ) => {
    const errors = await validateForm();
    if (Object.keys(errors).length === 0) {
      setCurrentStep(currentStep + 1);
    } else {
      setTouched({
        title: true,
        description: true,
        file: true as unknown as FormikTouched<File>
      });
    }
  };

  return (
    <FadeIn className="p-lg-4">
      <Stack
        gap={3}
        className="py-lg-0 px-4 py-2"
      >
        <div>
          <BackButton
            text={`Back to document${editing ? ' details' : 's'}`}
            color="#2B4E64"
            url={
              editing ? `/admin/content/documents/${data?.document.id}` : '/admin/content/documents'
            }
          />
          <h2
            className="d-none d-lg-block text-info fw-bold mb-0"
            style={{ fontSize: '2.25rem' }}
          >
            {editing ? 'Edit' : 'New'} Document
          </h2>
          {steps.length > 1 && (
            <Stepper
              steps={steps}
              currentStep={currentStep}
              editing={editing === true}
              onStepClick={(step) => setCurrentStep(step)}
            />
          )}
        </div>

        <div
          className="p-lg-5 p-3"
          style={{
            boxShadow: '0 4px 8px -2px rgba(16, 24, 40, .1),  0 4px 4px 0 rgba(0, 0, 0, .25)',
            border: '1px solid #EBEBEB',
            borderRadius: 10
          }}
        >
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchemas[currentStep]}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({
              submitForm,
              isSubmitting,
              validateForm,
              touched,
              errors,
              setTouched,
              values,
              setFieldValue
            }: FormikProps<FormValues>) => (
              <FormikForm className="text-start">
                <div style={{ minHeight: '65vh' }}>
                  {currentStep < steps.length &&
                    React.createElement(steps[currentStep]?.form, {
                      errors,
                      touched,
                      values,
                      setFieldValue
                    })}
                </div>

                <Row className="justify-content-end mt-3">
                  <Col
                    xs={6}
                    lg={3}
                  >
                    {currentStep !== 0 && (
                      <SimpleButton
                        disabled={isSubmitting}
                        type="button"
                        onClick={() => setCurrentStep(currentStep - 1)}
                        className="w-100"
                        variant="outline-primary"
                      >
                        Back
                      </SimpleButton>
                    )}
                  </Col>

                  <Col
                    xs={6}
                    lg={3}
                  >
                    <SimpleButton
                      disabled={isSubmitting}
                      type="button"
                      onClick={
                        currentStep === steps.length - 1
                          ? submitForm
                          : () => handleNext(validateForm, setTouched, values)
                      }
                      className="w-100"
                      variant="primary"
                    >
                      {currentStep === steps.length - 1 ? 'Finish' : 'Continue'}
                    </SimpleButton>
                  </Col>
                </Row>
              </FormikForm>
            )}
          </Formik>
        </div>
      </Stack>
    </FadeIn>
  );
};
